/**
 * §19 carve-out client-side guard. Mirrors the backend's
 * `commerce_banned_partner_tags` and the language list rejected by DB triggers
 * on products / discount-codes (per docs/api/admin/commerce/products.md and
 * docs/api/admin/commerce/discount-codes.md).
 *
 * Goals:
 *  1. Catch banned terms early so operators get instant feedback.
 *  2. Surface as a warm warning, not a red error — see plan.md §3.12.
 *  3. Cover three locales (en, ar, eg-ar). Strings are matched case-insensitively
 *     on a normalized form (NFKC + lowercase + Arabic-tatweel/diacritics stripped).
 *
 * Changes here must mirror the backend list. Two reviewers required per ADR.
 */

const BANNED_EN: readonly string[] = [
  'diet',
  'slimming',
  'fat loss',
  'fat-loss',
  'fat_loss',
  'bariatric',
  'thinspiration',
  'ozempic',
  'semaglutide',
  'appetite suppressant',
  'appetite-suppressant',
  'appetite_suppressant',
  'detox',
  'cleanse',
  'before after',
  'before/after',
  'before-after',
  'before_after',
  'weight loss',
  'shrinking',
];

// Arabic terms — both MSA and common Egyptian. Diacritics stripped before match.
const BANNED_AR: readonly string[] = [
  'رجيم',
  'دايت',
  'تنحيف',
  'تخسيس',
  'انقاص الوزن',
  'إنقاص الوزن',
  'حرق الدهون',
  'سيمجلوتايد',
  'اوزمبك',
  'أوزمبك',
  'كابح للشهية',
  'ديتوكس',
  'ديتوكس',
  'تطهير',
  'قبل وبعد',
];

// Retired framings — soft warnings, not hard errors. See CLAUDE.md.
const RETIRED_EN: readonly string[] = [
  "your size doesn't define you",
  'your size does not define you',
  'everybody included',
];
const RETIRED_AR: readonly string[] = [
  'مقاسك لا يحدّدك',
  'مقاسك لا يحددك',
  'آخر 100 مكان',
  'اخر 100 مكان',
];

const ARABIC_DIACRITICS = /[ً-ْٰـ]/g;

function normalize(input: string): string {
  return input
    .normalize('NFKC')
    .toLowerCase()
    .replace(ARABIC_DIACRITICS, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export type GuardSeverity = 'banned' | 'retired';

export interface GuardHit {
  term: string;
  severity: GuardSeverity;
}

const BANNED_NORMALIZED = [...BANNED_EN, ...BANNED_AR].map(normalize);
const RETIRED_NORMALIZED = [...RETIRED_EN, ...RETIRED_AR].map(normalize);
const BANNED_RAW = [...BANNED_EN, ...BANNED_AR];
const RETIRED_RAW = [...RETIRED_EN, ...RETIRED_AR];

export function scanContent(value: string | null | undefined): GuardHit[] {
  if (!value) return [];
  const haystack = normalize(value);
  if (!haystack) return [];
  const hits: GuardHit[] = [];
  for (let i = 0; i < BANNED_NORMALIZED.length; i++) {
    const needle = BANNED_NORMALIZED[i];
    if (needle && haystack.includes(needle)) {
      hits.push({ term: BANNED_RAW[i] ?? needle, severity: 'banned' });
    }
  }
  for (let i = 0; i < RETIRED_NORMALIZED.length; i++) {
    const needle = RETIRED_NORMALIZED[i];
    if (needle && haystack.includes(needle)) {
      hits.push({ term: RETIRED_RAW[i] ?? needle, severity: 'retired' });
    }
  }
  return hits;
}

/** Convenience: scan an arbitrary record of locale-keyed strings. */
export function scanLocaleFields(values: Record<string, string | null | undefined>): GuardHit[] {
  return Object.values(values).flatMap((v) => scanContent(v));
}
